import { useEffect, useState } from 'react';

interface MenuPhotoViewerProps {
  pages: string[];
  initialIndex?: number;
  onClose: () => void;
}

export function MenuPhotoViewer({ pages, initialIndex = 0, onClose }: MenuPhotoViewerProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, pages.length - 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pages.length]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(0, 0, 0, 0.94)',
        display: 'flex',
        flexDirection: 'column',
        padding: 'max(20px, env(safe-area-inset-top)) 16px max(20px, env(safe-area-inset-bottom))',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', paddingBottom: 12 }}>
        <span style={{ fontSize: 13, opacity: 0.75 }}>
          {pages.length > 1 ? `Page ${index + 1} of ${pages.length}` : 'Original menu'}
        </span>
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            background: 'rgba(255, 255, 255, 0.12)',
            border: 'none',
            color: '#fff',
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {pages[index] && (
          <img
            src={pages[index]}
            alt={`Menu page ${index + 1}`}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }}
          />
        )}
      </div>

      {pages.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, paddingTop: 12 }} onClick={(e) => e.stopPropagation()}>
          {pages.map((_, i) => (
            <button
              key={i}
              aria-label={`Page ${i + 1}`}
              onClick={() => setIndex(i)}
              style={{
                width: i === index ? 22 : 8,
                height: 8,
                borderRadius: 9999,
                border: 'none',
                background: i === index ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                transition: 'width 200ms ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
